import styled, { useTheme } from 'styled-components';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';
import ScrollRestore from '../../components/ScrollRestore';
import { Capacitor } from '@capacitor/core';
import {
  BodySmall,
  CaptionSmall,
  Heading4Text,
  ParagraphMediumText,
  ParagraphSmallText,
} from '../../components/Texts';
import NewCard from './NewCard';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Button from '../../components/Button';
import PrepaidCard from './PrepaidCard';
import { useEffect, useRef, useState } from 'react';
import Dots from './Dots';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/slices/modals';
import {
  useCheckFeeMutation,
  useListPrepaidCardsQuery,
} from '../../redux/api/authorized/zyptoPrepaidCards';
import {
  selectTokenBySymbol,
  selectTokensWithUSDBalance,
} from '../../redux/slices/tokenSlice';
import { formatUSDEnsure2Digits } from '../../common/currency';
import { getPrepaidCardFee } from '../../common/prepaidCards';
import ContentLoader from 'react-content-loader';
import { Browser } from '@capacitor/browser';
import { formatDate } from '../../common/date';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
  padding-bottom: 24px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 12px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 4px;
  }
`;

const StyledHeading4Text = styled(Heading4Text)`
  font-weight: 600;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: auto;
`;

const CardContainer = styled.div`
  width: 95%;
  padding: 3px;
  margin: 0 auto;
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const Cards = () => {
  const theme = useTheme();

  const [selectedCard, setSelectedCard] = useState(0);
  const carouselRef = useRef(null);
  const [selectedSlide, setSelectedSlide] = useState(1);
  const dispatch = useDispatch();
  const { data: prepaidCards } = useListPrepaidCardsQuery();
  const tokens = useSelector((state) => selectTokensWithUSDBalance(state));
  const usdcToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'USDC'),
  );

  useEffect(() => {
    if (prepaidCards?.cards?.length !== 0) {
      // auto select first card if it exists
      setSelectedSlide(2);
    }
  }, [prepaidCards]);

  const createPrepaidCard = async (amount, selectedToken, totalWithFee) => {
    dispatch(
      openModal({
        name: 'createPrepaidCard',
        props: {
          amount: amount,
          selectedToken: selectedToken,
          totalWithFee: totalWithFee,
        },
      }),
    );
  };

  const createPrepaidCardPrep = async (amount, totalWithFee) => {
    const tokensWithEnoughBalance = tokens.filter(
      (token) => token.usdValue >= totalWithFee,
    );

    if (tokensWithEnoughBalance.length === 0) {
      dispatch(
        openModal({
          name: 'addFunds',
        }),
      );
      return;
    }

    if (tokensWithEnoughBalance.length > 1) {
      dispatch(
        openModal({
          name: 'paymentSuggestion',
          props: {
            amount: totalWithFee,
            tokenOptions: tokensWithEnoughBalance,
            onPaymentMethodSelected: (paymentData) => {
              createPrepaidCard(amount, paymentData.token, totalWithFee);
            },
          },
        }),
      );
    } else {
      createPrepaidCard(amount, usdcToken, totalWithFee);
    }
  };

  const handleNewCard = () => {
    dispatch(
      openModal({
        name: 'amountSelector',
        props: {
          text: 'New Prepaid Card',
          onContinue: (amount, totalWithFee) => {
            createPrepaidCardPrep(amount, totalWithFee);
          },
          requiresFee: true,
          minAmount: 20,
          maxAmount: 1000,
          defaultAmount: 20,
          values: [20, 100, 500, 1000],
        },
      }),
    );
  };

  const cards = [
    {
      onClick: handleNewCard,
      component: (
        <CardContainer>
          <NewCard />
        </CardContainer>
      ),
      body: (
        <>
          <InfoContainer>
            <ParagraphMediumText style={{ color: theme.text.title }}>
              Prepaid Debit Card
            </ParagraphMediumText>
            <ParagraphSmallText style={{ color: theme.text.secondary }}>
              Fixed, prepaid balance. No KYC - perfect for quick spending.
            </ParagraphSmallText>
          </InfoContainer>
          <ButtonContainer>
            <Button onClick={handleNewCard} fullWidth>
              Buy Prepaid Card
            </Button>
          </ButtonContainer>
        </>
      ),
    },
  ];

  for (let index = 0; index < prepaidCards?.cards.length; index++) {
    const card = prepaidCards?.cards[index];
    cards.push({
      onClick: () => {
        if (
          Capacitor.isNativePlatform() &&
          Capacitor.isPluginAvailable('Browser')
        ) {
          Browser.open({
            url: `https://app.obsidian.cards/portal/login`,
            presentationStyle: 'popover',
          });
        } else {
          window.open(`https://app.obsidian.cards/portal/login`, '_blank');
        }
      },
      component: (
        <CardContainer>
          <PrepaidCard />
        </CardContainer>
      ),
      body: (
        <>
          <InfoContainer>
            <ParagraphMediumText style={{ color: theme.text.title }}>
              Prepaid card {index + 1}
            </ParagraphMediumText>
            <ParagraphSmallText style={{ color: theme.text.regular }}>
              {formatDate(new Date(card?.createdAt))}
            </ParagraphSmallText>
            <ParagraphSmallText style={{ color: theme.primary.primary }}>
              {formatUSDEnsure2Digits(card.amount)}
            </ParagraphSmallText>
          </InfoContainer>
        </>
      ),
    });
  }

  return (
    <Container>
      <ScrollRestore />
      <TitleContainer>
        <StyledHeading4Text style={{ color: theme.text.title }}>
          Cards
        </StyledHeading4Text>
      </TitleContainer>
      <Carousel
        ref={carouselRef}
        selectedItem={selectedSlide}
        onChange={(index) => {
          if (index === 0) {
            carouselRef?.current?.selectItem({
              selectedItem: 1,
              swiping: false,
            });
            return;
          }
          if (index > cards.length) {
            carouselRef?.current?.selectItem({
              selectedItem: cards.length,
              swiping: false,
            });
            return;
          }
          setSelectedSlide(index);
          setSelectedCard(index - 1);
        }}
        showThumbs={false}
        centerMode
        showIndicators={false}
        showStatus={false}
        centerSlidePercentage={85}
        onClickItem={(index) => {
          cards[index - 1].onClick();
        }}
      >
        <div key='spacer-left' />
        {cards.map((card) => card.component)}
        <div key='spacer-right' />
      </Carousel>
      <DotsContainer>
        <Dots currentDot={selectedCard} totalDots={cards.length} />
      </DotsContainer>
      {cards[selectedCard]?.body}
    </Container>
  );
};

export default Cards;
